import TabToggle from '../../components/shared/tab-toggle';
import ModalHeading from '../../components/shared/modal-heading';
import CustomReactSelect from '../../components/shared/custom-react-selector';
import { prependDefault } from '../../utils/structure-options-utils';
import React, { useState, useEffect } from 'react';
import Button from '../../components/shared/custom-btn';
import { updateAccount } from '../../services/auth-service';

const DataSettings = ({ data, units, accountId, setAttributes }) => {
    const [loading, setLoading] = useState(false);
    const [apiResponse, setApiResponse] = useState({ message: '', type: '' });

    // formData = attributes object from API
    const [formData, setFormData] = useState({
        currency: data?.currency || '',
        distanceUnit: data?.distanceUnit || 'km',
        speedUnit: data?.speedUnit || 'kmh',
        volumeUnit: data?.volumeUnit || 'l',
        weightUnit: data?.weightUnit || 'kg',
        notifyAdmin: data?.notifyAdmin || false,
        notifyManager: data?.notifyManager || false,
    });

    useEffect(() => {
        // update formData when data (attributes) changes from parent
        if (data) {
            setFormData((prev) => ({
                ...prev,
                ...data,
            }));
        }
    }, [data]);

    // Map string arrays to { value, label } format
    const mapToOptions = (values) => {
        return values?.map((v) => ({ value: v, label: v })) || [];
    };

    // Generate options with prependDefault for currency
    const currencyOptions = prependDefault(mapToOptions(units?.currency), 'Currency');
    const distanceUnitOptions = mapToOptions(units?.distanceUnit);
    const speedUnitOptions = mapToOptions(units?.speedUnit);
    const volumeUnitOptions = mapToOptions(units?.volumeUnit);
    const weightUnitOptions = mapToOptions(units?.weightUnit);

    const handleSelectChange = (selectedOption, { name }) => {
        setFormData((prev) => ({
            ...prev,
            [name]: selectedOption ? selectedOption.value : '',
        }));
        setApiResponse({ message: '', type: '' }); // Clear previous messages
    };

    const handleToggleChange = (value, name) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        setApiResponse({ message: '', type: '' }); // Clear previous messages
    };

    const handleSave = async () => {
        if (!formData.currency) {
            setApiResponse({ message: 'Please select a currency.', type: 'error' });
            return;
        }

        setLoading(true);
        setApiResponse({ message: '', type: '' });

        const payload = {
            attributes: { ...formData },
        };

        try {
            const response = await updateAccount(accountId, payload);
            console.log("Data : ", response.data)
            if (response.success) {
                const updatedAttributes = response.data.data.attributes;
                
                // Update localStorage with the new attributes from the response
                localStorage.setItem('accountAttributes', JSON.stringify(updatedAttributes));
                setApiResponse({ message: 'Account updated successfully!', type: 'success' });
                setAttributes(updatedAttributes);

                // ✅ Keep local state updated without refetch
                setFormData({ ...formData });
            } else {
                setApiResponse({ message: response.data.message || 'Failed to update Account.', type: 'error' });
            }
        } catch (error) {
            setApiResponse({ message: 'An unexpected error occurred.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div className="grid md:grid-cols-2 grid-cols-1 lg:gap-8 gap-4 md:pt-6 pt-4">
                <div>
                    <ModalHeading>Units</ModalHeading>
                    <p className="text-gray-700">Select units of measurements according to your preferences</p>
                </div>
                <div className="w-full ">
                    {/* API Response Message */}
                    {apiResponse.message && (
                        <div
                            className={`mb-4 p-3 rounded ${apiResponse.type === 'success'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                                }`}
                        >
                            {apiResponse.message}
                        </div>
                    )}

                    {/* Distance Unit Toggle */}
                    <div className="flex justify-between border-b gap-3 py-4 items-center w-full">
                        <h1 className="md:text-lg w-64">Distance Unit</h1>
                        <TabToggle
                            options={distanceUnitOptions}
                            className="lg:!w-[103px] !w-[20] "
                            selected={formData.distanceUnit}
                            onChange={(val) => handleToggleChange(val, 'distanceUnit')}
                            disabled={loading}
                        />
                    </div>

                    {/* Speed Unit Toggle */}
                    <div className="flex justify-between border-b gap-3 py-4 items-center w-full">
                        <h1 className="md:text-lg w-64">Speed Unit</h1>
                        <TabToggle
                            options={speedUnitOptions}
                            className="lg:!w-[103px] !w-[20] "
                            selected={formData.speedUnit}
                            onChange={(val) => handleToggleChange(val, 'speedUnit')}
                            disabled={loading}
                        />
                    </div>

                    {/* Volume Unit Toggle */}
                    <div className="flex justify-between border-b gap-3 py-4 items-center w-full">
                        <h1 className="md:text-lg w-64">Volume Unit</h1>
                        <TabToggle
                            options={volumeUnitOptions}
                            className="lg:!w-[103px] !w-[20] "
                            selected={formData.volumeUnit}
                            onChange={(val) => handleToggleChange(val, 'volumeUnit')}
                            disabled={loading}
                        />
                    </div>

                    {/* Weight Unit Toggle */}
                    <div className="flex justify-between py-4 items-center w-full">
                        <h1 className="md:text-lg w-64">Weight Unit</h1>
                        <TabToggle
                            options={weightUnitOptions}
                            className="lg:!w-[103px] !w-[20] "
                            selected={formData.weightUnit}
                            onChange={(val) => handleToggleChange(val, 'weightUnit')}
                            disabled={loading}
                        />
                    </div>
                </div>
            </div>

            <div className="grid md:grid-cols-2 grid-cols-1 lg:gap-8 gap-4 md:py-6 py-4 border-t">
                <div>
                    <ModalHeading>Locale</ModalHeading>
                    <p className="text-gray-700">Select your account level currency</p>
                </div>

                {/* Currency Selector */}
                <div className="flex justify-between gap-3 py-4 items-center w-full">
                    <h1 className="md:text-lg w-64">Currency</h1>
                    <div className="lg:min-w-52 min-w-40 w-fit max-w-full">
                        <CustomReactSelect
                            name="currency"
                            value={currencyOptions.find((opt) => opt.value === formData.currency) || null}
                            onChange={handleSelectChange}
                            options={currencyOptions}
                            required={true}
                            isDisabled={loading}
                        />
                    </div>
                </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end mt-6 ">
                <Button
                    onClick={handleSave}
                    disabled={loading}
                    loading={loading}
                    label="Save"
                    loadingLabel="Saving..."
                    className={`px-6 w-52 py-2 rounded-lg text-white font-semibold bg-btnBg`}
                />
            </div>
        </div>
    );
};

export default DataSettings;
